import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { resetPassword, formatApiErrorDetail } from "@/lib/api";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Those passwords don't match — try again.");
      return;
    }
    setStatus("loading");
    try {
      await resetPassword(token, password);
      setStatus("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#FFFDF6] px-4" data-testid="reset-page">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#FEF3C7] blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#E0F2FE] blur-3xl" />
        <div className="rainbow-arc absolute -bottom-40 left-1/2 h-[26rem] w-[48rem] -translate-x-1/2 opacity-60" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative w-full max-w-md rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-xl"
      >
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#FFC107]">
          {status === "done" ? (
            <CheckCircle2 className="h-7 w-7 text-white" />
          ) : (
            <KeyRound className="h-7 w-7 text-white" />
          )}
        </span>
        {status === "done" ? (
          <div className="mt-5 text-center" data-testid="reset-success">
            <h1 className="font-display text-2xl font-bold text-slate-900">All set!</h1>
            <p className="mt-2 text-sm text-slate-600">
              Your password is updated. Sign in with your new one.
            </p>
            <Link
              to="/signin"
              data-testid="reset-signin-link"
              className="mt-6 inline-block rounded-full bg-slate-900 px-8 py-3.5 font-display text-base font-semibold text-white"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mt-5 text-center font-display text-2xl font-bold text-slate-900">
              Choose a new password
            </h1>
            {!token && (
              <p className="mt-3 rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="reset-no-token">
                This link is missing its token — please use the link from your email.
              </p>
            )}
            <form onSubmit={submit} className="mt-6 grid gap-3" data-testid="reset-form">
              <input
                required
                type="password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                placeholder="New password (8+ characters)"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              <input
                required
                type="password"
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                placeholder="Confirm new password"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              {error && (
                <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="reset-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={!token || status === "loading"}
                data-testid="reset-submit-btn"
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-display text-base font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
              >
                {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
                Save new password
              </button>
            </form>
            <p className="mt-5 text-center text-xs text-slate-400">
              <Star className="mr-1 inline h-3 w-3 fill-[#FFC107] text-[#FFC107]" />
              Little Songs. Big Faith.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
